import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, ChevronLeft, Cloud, File as FileIcon, FileText, Film, Folder, Image, Loader2, Music, X } from 'lucide-react';
import { cn, formatFileSize, ui } from '../../utils';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthStore } from '../../stores/authStore';
import { useFocusTrap } from '../../hooks/useFocusTrap';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

interface OneDriveItem {
  id: string;
  name: string;
  size?: number;
  lastModifiedDateTime?: string;
  folder?: { childCount: number };
  file?: { mimeType: string };
}

interface FolderEntry {
  id: string;
  name: string;
}

interface OneDrivePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onFilesSelected: (files: File[]) => void;
}

function formatDate(isoDate?: string) {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getFileIcon(item: OneDriveItem) {
  if (item.folder) return <Folder className="h-5 w-5 text-yellow-400" />;
  const mime = item.file?.mimeType || '';
  const name = item.name.toLowerCase();
  if (mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|heic|svg)$/.test(name)) return <Image className="h-5 w-5 text-green-400" />;
  if (mime.includes('pdf') || name.endsWith('.pdf')) return <FileText className="h-5 w-5 text-red-400" />;
  if (mime.includes('video') || /\.(mp4|mov|avi|wmv|webm)$/.test(name)) return <Film className="h-5 w-5 text-purple-400" />;
  if (mime.includes('audio') || /\.(mp3|wav|m4a|webm)$/.test(name)) return <Music className="h-5 w-5 text-pink-400" />;
  return <FileIcon className="h-5 w-5 text-muted-foreground" />;
}

export function OneDrivePicker({ isOpen, onClose, onFilesSelected }: OneDrivePickerProps) {
  const { getAccessToken } = useAuth();
  const sessionToken = useAuthStore((state) => state.token);
  const [items, setItems] = useState<OneDriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [folderStack, setFolderStack] = useState<FolderEntry[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const modalRef = useFocusTrap<HTMLDivElement>(isOpen, onClose);

  const currentFolderId = folderStack[folderStack.length - 1]?.id;

  const getBearerToken = useCallback(async () => {
    return (await getAccessToken()) || sessionToken || null;
  }, [getAccessToken, sessionToken]);

  const fetchGraphJson = useCallback(async (graphPath: string) => {
    const token = await getBearerToken();
    if (!token) {
      throw new Error('Sign in again to browse OneDrive files.');
    }

    const response = await fetch(`${GRAPH_BASE}${graphPath}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(payload?.error?.message || 'Failed to load OneDrive files');
    }

    return payload;
  }, [getBearerToken]);

  const fetchItems = useCallback(
    async (folderId?: string) => {
      setLoading(true);
      setError(null);
      try {
        const graphPath = folderId
          ? `/me/drive/items/${encodeURIComponent(folderId)}/children?$top=200&$select=id,name,size,lastModifiedDateTime,folder,file`
          : '/me/drive/root/children?$top=200&$select=id,name,size,lastModifiedDateTime,folder,file';
        const data = await fetchGraphJson(graphPath);
        setItems(Array.isArray(data?.value) ? data.value : []);
      } catch (err) {
        setItems([]);
        setError(err instanceof Error ? err.message : 'Failed to load OneDrive files');
      } finally {
        setLoading(false);
      }
    },
    [fetchGraphJson],
  );

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds(new Set());
    setFolderStack([]);
    void fetchItems();
  }, [fetchItems, isOpen]);

  const navigateToFolder = useCallback(
    (item: OneDriveItem) => {
      setSelectedIds(new Set());
      setFolderStack((current) => [...current, { id: item.id, name: item.name }]);
      void fetchItems(item.id);
    },
    [fetchItems],
  );

  const navigateBack = useCallback(() => {
    if (folderStack.length === 0) return;
    setSelectedIds(new Set());
    const next = folderStack.slice(0, -1);
    setFolderStack(next);
    void fetchItems(next[next.length - 1]?.id);
  }, [fetchItems, folderStack]);

  const sortedItems = useMemo(
    () => [...items].sort((left, right) => {
      if (left.folder && !right.folder) return -1;
      if (!left.folder && right.folder) return 1;
      return left.name.localeCompare(right.name);
    }),
    [items],
  );

  const selectedFileCount = useMemo(
    () => [...selectedIds].filter((id) => {
      const item = items.find((candidate) => candidate.id === id);
      return item && !item.folder;
    }).length,
    [items, selectedIds],
  );

  const attachFiles = useCallback(async () => {
    const filesToDownload = items.filter((item) => selectedIds.has(item.id) && !item.folder);
    if (filesToDownload.length === 0) return;

    setDownloading(true);
    setError(null);
    try {
      const token = await getBearerToken();
      if (!token) {
        throw new Error('Sign in again to browse OneDrive files.');
      }

      const files: File[] = [];
      for (const item of filesToDownload) {
        const response = await fetch(`${GRAPH_BASE}/me/drive/items/${encodeURIComponent(item.id)}/content`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error?.message || `Failed to download ${item.name}`);
        }

        const blob = await response.blob();
        files.push(new File([blob], item.name, { type: item.file?.mimeType || blob.type || 'application/octet-stream' }));
      }
      onFilesSelected(files);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download selected files');
    } finally {
      setDownloading(false);
    }
  }, [getBearerToken, items, onClose, onFilesSelected, selectedIds]);

  if (!isOpen) return null;

  return (
    <div className={ui.modalBackdrop} onClick={(event) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) onClose();
    }}>
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onedrive-picker-title"
        tabIndex={-1}
        className={cn(ui.modalPanel, 'flex max-h-[70vh] w-full max-w-lg flex-col overflow-hidden')}
      >
        <div className="flex items-center justify-between border-b border-border bg-background/80 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            {folderStack.length > 0 ? (
              <button type="button" onClick={navigateBack} className="flex min-h-[44px] items-center gap-1 rounded-xl px-2 text-sm text-muted-foreground transition-colors duration-150 hover:bg-background hover:text-foreground" aria-label="Go back to previous folder">
                <ChevronLeft className="h-4 w-4" />
                <span className="max-w-[100px] truncate">{folderStack[folderStack.length - 2]?.name || 'OneDrive'}</span>
              </button>
            ) : null}
            <div className="flex min-w-0 items-center gap-2">
              <Cloud className="h-5 w-5 shrink-0 text-blue-400" />
              <h2 id="onedrive-picker-title" className="truncate text-sm font-semibold text-foreground">{folderStack[folderStack.length - 1]?.name || 'OneDrive'}</h2>
            </div>
          </div>
          <button type="button" onClick={onClose} className={cn(ui.button.icon, 'h-10 w-10')} aria-label="Close OneDrive picker">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
              <span className="ml-2 text-sm text-muted-foreground">Loading files...</span>
            </div>
          ) : null}

          {!loading && error ? (
            <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
              <AlertCircle className="mb-2 h-8 w-8 text-red-300" />
              <p className="text-sm text-red-200">{error}</p>
              <button type="button" onClick={() => void fetchItems(currentFolderId)} className="mt-3 text-sm text-blue-300 underline underline-offset-2 transition-colors duration-150 hover:text-blue-200" aria-label="Retry loading OneDrive files">
                Try again
              </button>
            </div>
          ) : null}

          {!loading && !error && sortedItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Folder className="mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">This folder is empty</p>
            </div>
          ) : null}

          {!loading && !error && sortedItems.length > 0 ? (
            <div className="divide-y divide-border/70">
              {sortedItems.map((item) => {
                const isFolder = Boolean(item.folder);
                const isSelected = selectedIds.has(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={cn(
                      'flex min-h-[56px] w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-background',
                      isSelected && !isFolder && 'bg-blue-500/10 hover:bg-blue-500/15',
                    )}
                    onClick={() => {
                      if (isFolder) {
                        navigateToFolder(item);
                        return;
                      }
                      setSelectedIds((current) => {
                        const next = new Set(current);
                        if (next.has(item.id)) next.delete(item.id);
                        else next.add(item.id);
                        return next;
                      });
                    }}
                    aria-pressed={!isFolder ? isSelected : undefined}
                    aria-label={isFolder ? `Open folder ${item.name}` : `${isSelected ? 'Deselect' : 'Select'} ${item.name}`}
                  >
                    <div className="flex w-5 shrink-0 items-center justify-center">
                      {!isFolder && isSelected ? (
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      ) : getFileIcon(item)}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-foreground">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {isFolder
                          ? `${item.folder?.childCount ?? 0} items`
                          : [formatFileSize(item.size ?? 0), formatDate(item.lastModifiedDateTime)].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {selectedFileCount > 0 ? (
          <div className="border-t border-border bg-background/80 px-4 py-3">
            <button
              type="button"
              onClick={() => void attachFiles()}
              disabled={downloading}
              className={cn(ui.button.base, ui.button.primary, 'flex w-full px-4 py-2.5')}
              aria-label={`Attach ${selectedFileCount} selected OneDrive file${selectedFileCount > 1 ? 's' : ''}`}
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {downloading ? 'Downloading...' : `Attach ${selectedFileCount} file${selectedFileCount > 1 ? 's' : ''}`}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default OneDrivePicker;
